import React, { useState, useEffect } from "react";
import { useApi } from "../../../hooks/useApi";

export default function FormularioDinamico({ slug, usuario, onGuardadoExitoso, onCancelar }) {
  const { request } = useApi();
  const [formulario, setFormulario] = useState(null);
  const [respuestas, setRespuestas] = useState({});
  const [loading, setLoading] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);

  // 1. Cargar la estructura del formulario desde la Base de Datos con validación blindada
  useEffect(() => {
    const cargarEstructura = async () => {
      setLoading(true);
      const response = await request(`/formularios-dinamicos/${slug}`);
      
      if (response && response.success && response.data) {
        const datosForm = response.data;
        setFormulario(datosForm);
        
        const respIniciales = {};
        if (Array.isArray(datosForm.campos)) {
          datosForm.campos.forEach(c => {
            respIniciales[c.nombre_campo] = c.tipo_componente === 'checkbox' ? false : '';
          });
        }
        setRespuestas(respIniciales);
      } else {
        setError("No se pudo cargar el formulario o la estructura está vacía.");
      }
      setLoading(false);
    };

    if (slug) cargarEstructura();
  }, [slug]); // eslint-disable-next-line react-hooks/exhaustive-deps

  const handleChange = (nombre_campo, valor) => {
    setRespuestas(prev => ({ ...prev, [nombre_campo]: valor }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setEnviando(true);

    const documento = usuario?.documento || usuario?.nit || "SIN_DOC";

    const payload = {
      usuario_documento: documento,
      datos_json: respuestas,
      firma_digital: null,
      observaciones_generales: respuestas.observaciones || ""
    };

    const res = await request(`/formularios-dinamicos/${slug}/respuestas`, "POST", payload);

    if (res && res.success) {
      if (onGuardadoExitoso) onGuardadoExitoso(res);
    } else {
      alert("Error al guardar el formulario. Intenta nuevamente.");
    }
    setEnviando(false);
  };

  const renderCampo = (campo) => {
    switch (campo.tipo_componente) {
      case "text":
      case "number":
      case "date":
        return (
          <input
            type={campo.tipo_componente}
            required={campo.requerido === 1}
            value={respuestas[campo.nombre_campo] || ""}
            onChange={(e) => handleChange(campo.nombre_campo, e.target.value)}
            className="w-full p-3.5 bg-white border border-slate-300 rounded-xl text-slate-900 font-medium focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all shadow-inner"
            placeholder={`Ingrese ${campo.etiqueta.toLowerCase()}`}
          />
        );
      
      case "textarea":
        return (
          <textarea
            required={campo.requerido === 1}
            value={respuestas[campo.nombre_campo] || ""}
            onChange={(e) => handleChange(campo.nombre_campo, e.target.value)}
            className="w-full p-3.5 bg-white border border-slate-300 rounded-xl text-slate-900 font-medium focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all shadow-inner"
            rows="3"
            placeholder="Escriba aquí..."
          />
        );

      case "select":
        return (
          <select
            required={campo.requerido === 1}
            value={respuestas[campo.nombre_campo] || ""}
            onChange={(e) => handleChange(campo.nombre_campo, e.target.value)}
            className="w-full p-3.5 bg-white border border-slate-300 rounded-xl text-slate-900 font-medium focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all shadow-inner cursor-pointer"
          >
            <option value="">Seleccione una opción...</option>
            {Array.isArray(campo.opciones) && campo.opciones.map((opc, i) => (
              <option key={i} value={opc}>{opc}</option>
            ))}
          </select>
        );

      case "firma":
        return (
          <div className="w-full h-32 border-2 border-dashed border-slate-300 rounded-xl bg-white/70 flex items-center justify-center text-slate-400 font-medium">
            (Área de Firma Digital en construcción ✍️)
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="w-full p-10 flex flex-col items-center justify-center animate-pulse">
        <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-3"></div>
        <p className="text-slate-700 font-bold text-sm">Construyendo formulario...</p>
      </div>
    );
  }

  if (error || !formulario) {
    return (
      <div className="bento-card p-6 bg-red-50/80 border-red-200 text-red-700 text-center rounded-2xl">
        <p className="font-bold">⚠️ {error}</p>
        <button onClick={onCancelar} className="mt-4 px-4 py-2 bg-slate-900 text-white rounded-full text-xs font-bold btn-interactive cursor-pointer">Volver</button>
      </div>
    );
  }

  return (
    <div className="w-full max-w-3xl mx-auto glass-panel p-6 sm:p-10 rounded-[32px] animate-fade-in-up border border-white/80 shadow-2xl">
      <div className="mb-8 border-b border-slate-200/60 pb-6">
        <span className="text-[10px] font-black px-3 py-1 bg-blue-100 text-blue-700 rounded-lg uppercase tracking-widest mb-3 inline-block shadow-sm">
          MÓDULO: {formulario.codigo_modulo?.toUpperCase()}
        </span>
        <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">{formulario.titulo}</h2>
        {formulario.descripcion && <p className="text-sm text-slate-600 font-medium mt-2">{formulario.descripcion}</p>}
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {Array.isArray(formulario.campos) && formulario.campos.map((campo) => (
          <div key={campo.id} className="p-5 bg-white/80 backdrop-blur-md border border-white rounded-2xl shadow-sm transition-all hover:bg-white/95">
            <label className="block text-sm font-extrabold text-slate-900 mb-2 ml-1 flex items-center gap-1">
              {campo.etiqueta}
              {campo.requerido === 1 && <span className="text-red-500">*</span>}
            </label>
            {renderCampo(campo)}
          </div>
        ))}

        <div className="flex gap-4 pt-6 mt-6 border-t border-slate-200/60">
          <button 
            type="button" 
            onClick={onCancelar}
            className="flex-1 py-4 rounded-2xl bg-white/80 text-slate-700 font-bold border border-slate-300 hover:bg-white transition-all btn-interactive cursor-pointer shadow-sm"
          >
            Cancelar
          </button>
          
          <button 
            type="submit" 
            disabled={enviando}
            className="flex-1 py-4 rounded-2xl bg-slate-900 text-white font-black uppercase tracking-wider hover:bg-slate-800 transition-all shadow-xl disabled:opacity-50 btn-interactive cursor-pointer"
          >
            {enviando ? 'Guardando...' : 'Guardar Formulario'}
          </button>
        </div>
      </form>
    </div>
  );
}